import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useNavigate } from 'react-router-dom';
import ProcessNavBar from '../../../utils/ProcessNavBar.js';

const GameNavBar = () => {
    const {
        masterPoolId,
        gameId,
        page,
        pickIndex,
        gameData,
        isUserInThisPool,
        isUserAdmin,
        masterPoolData,
        isThisPoolPartyGame,
        unsavedPicks,
        unsavedBracketPicks,
        setUnsavedPicksModalOpen,
        setUrlForNav,
    } = useUserAndGameContext();

    const [processedNav, setProcessedNav] = useState(null);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (masterPoolData?.navBarInfo) {
            const processed = ProcessNavBar(masterPoolData.navBarInfo, {
                isUserInThisPool,
                isUserAdmin,
                isThisPoolPartyGame,
                gameData
            });
            setProcessedNav(processed);
        }
    }, [gameId, masterPoolId, masterPoolData, isUserInThisPool, isUserAdmin, isThisPoolPartyGame, gameData]);

    // Check if content is scrollable
    useEffect(() => {
        const checkScrollable = () => {
            if (scrollContainerRef.current) {
                const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
                const canScrollRight = scrollWidth > clientWidth && scrollLeft < (scrollWidth - clientWidth - 5);
                setShowScrollIndicator(canScrollRight);
            }
        };

        // Check on mount and when nav items change
        checkScrollable();

        // Add scroll listener to update indicator when user scrolls
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', checkScrollable);
            // Also check on resize
            window.addEventListener('resize', checkScrollable);
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', checkScrollable);
            }
            window.removeEventListener('resize', checkScrollable);
        };
    }, [processedNav]);

    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:767px)');
    const navigate = useNavigate();

    // Get Nav Bar Info from Master Pool Data
    const navBarInfo = masterPoolData?.navBarInfo;

    // Early return if no navBarInfo
    if (!navBarInfo) {
        return null;
    }

    const handleNavClick = (navPage) => {
        const queryParams = new URLSearchParams();
        queryParams.set('mId', masterPoolId);
        queryParams.set('gId', gameId);
        queryParams.set('page', navPage);
        if (pickIndex) {
            queryParams.set('pickIndex', pickIndex);
        }

        const newUrl = `?${queryParams.toString()}`;

        if (unsavedPicks || unsavedBracketPicks) {
            setUrlForNav(newUrl);
            setUnsavedPicksModalOpen(true);
            return;
        }

        navigate(newUrl);
    };

    return (
        <Box
            sx={{
                backgroundColor: navBarInfo.navBarBackgroundColor || '#FFFFFF',
                width: '100%',
                position: 'relative',
                height: '50px',
                display: 'flex',
                alignItems: 'center'
            }}
        >
            {/* Scrollable navigation container */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    flex: 1,
                    height: '100%',
                    overflowX: 'auto',
                    // Hide scrollbar but keep functionality
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: { xs: 'flex-start', sm: 'center' },
                        minWidth: '100%',
                        height: '100%',
                        gap: isMobile ? '12px' : '24px',
                        paddingLeft: '16px',
                        paddingRight: showScrollIndicator ? '40px' : '16px' // Extra space when chevron shows
                    }}
                >
                    {processedNav?.items?.map((item, index) => {
                        const isActive = page === item.page;
                        return (
                            <Box
                                key={`${item.page}-${index}`}
                                onClick={() => handleNavClick(item.page)}
                                sx={{
                                    cursor: 'pointer',
                                    color: isActive ? '#07356B' : '#373737',
                                    padding: '0px',
                                    whiteSpace: 'nowrap',
                                    height: '65%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    flex: 'none',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: 'rgba(7, 53, 107, 0.05)'
                                    },
                                    '&::after': isActive ? {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '2px',
                                        flexShrink: 0,
                                        alignSelf: 'stretch',
                                        borderRadius: '1px',
                                        backgroundColor: '#07356B',
                                    } : {}
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: isMobile ? '12px' : '16px',
                                        fontWeight: isActive ? 700 : 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        padding: '0 8px'
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* Chevron with integrated fade effect */}
            {showScrollIndicator && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: '0px',
                        top: '0px',
                        height: '100%',
                        width: '35px',
                        background: `linear-gradient(to right, transparent, ${navBarInfo.navBarBackgroundColor || '#FFFFFF'} 50%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        zIndex: 2,
                        paddingRight: '4px'
                    }}
                >
                    <Box
                        sx={{
                            color: 'rgba(55, 55, 55)',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            marginTop: '-4px',
                        }}
                    >
                        ›
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default GameNavBar;